import { PageDrawer } from '@app/components/page/drawer/PageDrawer';
import { PlaybackGuard } from '@app/core/playback/context/PlaybackGuard';
import { useIntl } from 'react-intl';
import { Page } from '../../../../../components/page/Page';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { useActiveStation } from '../../../../../core/stations/hooks/useActiveStation';
import { formatStreamStartForTitle } from '../helpers/format-stream-start-for-title';
import { useStreamUserDefinedRange } from '../stores/stream-screen.store';
import { StreamDrawerContent } from './drawer/StreamDrawerContent';
import { useStreamDrawer } from './drawer/useStreamDrawer';

interface Props {
  readonly children: React.ReactNode;
}

/**
 * Wraps the Logger Stream content with the shared Page layout:
 * document title (station + range date/time + "Logger"), and tracking id.
 * Stream hotkeys are registered in LoggerScreenSetup (inside Page so drawer context is available).
 * First child is the header; use Page.Content for the main content.
 * Drawer open state and content type are persisted in stream screen settings store.
 */
export function LoggerStreamPageLayout({ children }: Props) {
  const { locale } = useIntl();
  const station = useActiveStation();
  const tz = useTimezone();
  const { from } = useStreamUserDefinedRange();
  const adapter = useStreamDrawer();

  const dateTimePart = from && tz ? formatStreamStartForTitle(from, locale, tz) : '';
  const htmlTitle = [station.name, dateTimePart, 'Logger'].filter(Boolean);

  return (
    <Page htmlTitle={htmlTitle}>
      <PageDrawer
        drawerId="logger-stream"
        drawerContent={
          <PlaybackGuard>
            <StreamDrawerContent />
          </PlaybackGuard>
        }
        {...adapter}
      >
        {children}
      </PageDrawer>
    </Page>
  );
}
